import React, { useRef, useLayoutEffect, useCallback, useState } from 'react';
import styled from 'styled-components';
import { blockPadV } from 'theme/mixins/padding';
import type { HorizontalSlideshow as T } from './types';
import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import TinaImage from 'components/TinaImage';
import { Maybe } from '.tina/__generated__/types';

const Wrapper = styled.div`
  ${() => blockPadV({ pt: 'no', pb: 'no' })};
  background: ${({ theme }) => theme.themeColors.bg};
`;

interface Container {
  carousel2: boolean;
  specialBottomPadding?: Maybe<boolean> | undefined;
}

const Container = styled.div<Container>`
  background: ${({ theme }) => theme.themeColors.bg};
  height: ${({ carousel2 }) => (carousel2 ? '366px' : '175px')};
  ${({ theme, carousel2 }) => theme.mq.lg`
    height: ${carousel2 ? '732px' : '350px'};
  `}
  // these equate to the 'lg' padding size in the cms
  ${({ specialBottomPadding }) => specialBottomPadding && `margin-bottom: 3.75rem;`}
  ${({ specialBottomPadding, theme }) => theme.mq.md`
    ${specialBottomPadding && `margin-bottom: 5.675rem;`}
  `}
`;

const AnimationContainer = styled(motion.div)`
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100vw;
  overflow: hidden;
  ${({ theme }) => theme.mq.lg`
    gap: 2rem;
  `}
`;

const CarouselWrapper = styled(motion.div)`
  position: relative;
  display: inline-block;
  padding: 0;
  margin: 0;
  @-moz-document url-prefix() {
    width: max-content;
  }
`;

const Carousel = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  height: 175px;
  ${({ theme }) => theme.mq.lg`
    height: 350px;
    gap: 2rem;
  `}
`;

const CarouselItem = styled(TinaImage)`
  height: 100%;
  border-radius: 1rem;
  // this fixes the images being streched on safari
  object-fit: contain;
`;

const Ghost = styled.div`
  width: 100vw;
`;

const HorizontalSlideshow: React.FC<T> = ({ carousel1, carousel2, specialBottomPadding }: T) => {
  const topRef = useRef(null);
  const bottomRef = useRef(null);
  const ghostRef = useRef(null);
  const [scrollRange, setScrollRange] = useState(0);
  const [viewportW, setViewportW] = useState(0);

  const { scrollYProgress } = useScroll();

  const physics = { damping: 15, mass: 0.27, stiffness: 55 };
  const topTransform = useTransform(scrollYProgress, [1, 0], [0, -scrollRange + viewportW]);
  const topSpring = useSpring(topTransform, physics);
  const bottomTransform = useTransform(scrollYProgress, [0, 1], [0, -scrollRange + viewportW]);
  const bottomSpring = useSpring(bottomTransform, physics);

  const onResize = useCallback((entries: ResizeObserverEntry[]) => {
    for (const entry of entries) {
      setViewportW(entry?.contentRect?.width);
    }
  }, []);

  useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver(entries => onResize(entries));
    ghostRef?.current && resizeObserver.observe(ghostRef?.current);
    return () => resizeObserver.disconnect();
  }, [onResize]);

  useLayoutEffect(() => {
    topRef?.current?.['scrollWidth'] && setScrollRange(topRef?.current['scrollWidth']);
  }, [topRef?.current?.['scrollWidth']]);

  return (
    <Wrapper>
      <Container carousel2={carousel2 ? true : false} specialBottomPadding={specialBottomPadding}>
        <AnimationContainer
          initial={{ opacity: 0 }}
          animate="show"
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ type: 'spring', stiffness: 50, opacity: { duration: 1.4 } } as any}
        >
          {carousel1 && (
            <CarouselWrapper ref={topRef} style={{ x: topSpring }}>
              <Carousel>
                {carousel1.map(
                  (carouselItem, idx) =>
                    carouselItem?.carousel1List && (
                      <CarouselItem
                        alt={'carousel item'}
                        src={carouselItem.carousel1List}
                        key={idx}
                      />
                    )
                )}
              </Carousel>
            </CarouselWrapper>
          )}
          {carousel2 && (
            <CarouselWrapper ref={bottomRef} style={{ x: bottomSpring }}>
              <Carousel>
                {carousel2.map(
                  (carouselItem, idx) =>
                    carouselItem?.carousel2List && (
                      <CarouselItem
                        alt={'carousel item'}
                        src={carouselItem.carousel2List}
                        key={idx}
                      />
                    )
                )}
              </Carousel>
            </CarouselWrapper>
          )}
        </AnimationContainer>
        <Ghost ref={ghostRef} />
      </Container>
    </Wrapper>
  );
};

export default HorizontalSlideshow;
